import React from 'react';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import { Loader2 } from 'lucide-react';
import { TreasurySummaryProps } from '../types';

/**
 * Итог одной полосой: касса, долг, прибыль и сходится ли с выпиской.
 * Под ней — из чего сложилась касса, чтобы цифру можно было проверить глазами.
 */
export function TreasurySummary({
  isDark,
  overview,
  isLoading,
  formatMoney,
}: TreasurySummaryProps) {
  if (isLoading || !overview) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const { cash, totalDebt, distributableProfit, bankCheck } = overview;

  const cardCls = cn(
    'rounded-2xl border-0 p-5 lg:p-6 transition-all duration-300',
    isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
  );

  const cell = (
    label: string,
    value: string,
    hint: string,
    tone?: 'debt' | 'profit' | 'ok' | 'error',
  ) => (
    <div>
      <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{label}</p>
      <p
        className={cn(
          'text-lg lg:text-xl font-bold mt-1',
          tone === 'debt' && 'text-amber-500',
          tone === 'profit' && 'text-emerald-500',
          tone === 'ok' && 'text-emerald-500',
          tone === 'error' && 'text-red-500',
          !tone && (isDark ? 'text-white' : 'text-[#111111]'),
        )}
      >
        {value}
      </p>
      <p className={cn('text-xs mt-1', isDark ? 'text-slate-500' : 'text-slate-400')}>{hint}</p>
    </div>
  );

  const part = (label: string, value: number, sign: '+' | '−') => (
    <span className={cn('whitespace-nowrap', isDark ? 'text-slate-400' : 'text-slate-500')}>
      <span className={sign === '+' ? 'text-emerald-500' : 'text-slate-400'}>{sign}</span> {label}{' '}
      <span className={cn('font-medium', isDark ? 'text-slate-200' : 'text-slate-700')}>
        {formatMoney(value)}
      </span>
    </span>
  );

  return (
    <Card className={cn(cardCls, 'ring-1 ring-[#C9A227]/40')}>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cell('Касса', formatMoney(cash.total), 'Деньги на счету прямо сейчас')}
        {cell(
          'Долг перед основателями',
          formatMoney(totalDebt),
          'Вложено и ещё не возвращено',
          totalDebt > 0 ? 'debt' : undefined,
        )}
        {cell(
          'Прибыль к распределению',
          formatMoney(distributableProfit),
          totalDebt > 0 ? 'Ноль, пока вклады не возвращены' : 'Касса за вычетом долгов',
          distributableProfit > 0 ? 'profit' : undefined,
        )}
        {bankCheck
          ? cell(
              'Сверка с выпиской',
              bankCheck.discrepancy === 0 ? 'сходится' : formatMoney(bankCheck.discrepancy),
              `Выписка на ${bankCheck.date}: ${formatMoney(bankCheck.amount)}`,
              bankCheck.discrepancy === 0 ? 'ok' : 'error',
            )
          : cell('Сверка с выпиской', '—', 'Остаток по выписке ещё не вводили')}
      </div>

      <div
        className={cn(
          'mt-5 pt-4 border-t flex flex-wrap items-center gap-x-5 gap-y-2 text-xs',
          isDark ? 'border-slate-700' : 'border-slate-200',
        )}
      >
        <span className={cn('font-medium', isDark ? 'text-slate-300' : 'text-slate-600')}>
          Касса сложилась из:
        </span>
        {part('поступило', cash.settled, '+')}
        {part('взносы', cash.contributions, '+')}
        {part('расходы со счёта', cash.companyExpenses, '−')}
        {part('возвраты', cash.reimbursements, '−')}
        {part('доли прибыли', cash.profitPayouts, '−')}
      </div>
    </Card>
  );
}
